import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { InstallRetryResult, InstallState } from '@volley/crucible-ipc-types/ipc-channels';
import type { ProjectRef } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

export function installStateQueryKey(
  client: Pick<ReturnType<typeof useCrucibleClient>, 'queryScope'>,
  gameId: string,
  project?: ProjectRef,
): readonly unknown[] {
  return projectQueryKey(client, ['install-state', gameId], project);
}

export interface InstallStateForGame {
  data: InstallState | null | undefined;
  isLoading: boolean;
  isRetrying: boolean;
  retryError: string | null;
  retryInstall: () => Promise<InstallRetryResult>;
  refresh: () => Promise<void>;
}

/**
 * Shared renderer owner for a game's persisted install-state record.
 *
 * Dashboard chips can keep their compact local surface, while Workspace can
 * compose install retry with preview start without letting a second retry state
 * hide inside `<InstallStateChip>`.
 */
export function useInstallStateForGame(
  gameId: string | undefined,
  options: { enabled?: boolean; project?: ProjectRef } = {},
): InstallStateForGame {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  const [retryStateByGame, setRetryStateByGame] = useState<
    Map<string, { retrying: boolean; error: string | null }>
  >(new Map());
  // Render state / query-cache status lag by a cycle; two same-tick
  // `retryInstall()` calls can both pass a state-based guard. Track in-flight
  // work synchronously per game so a route switch neither leaks A's status
  // into B nor unnecessarily serialises installs for unrelated games.
  const inFlightGamesRef = useRef<Set<string>>(new Set());
  const enabled = (options.enabled ?? true) && !!gameId;
  const currentRetryState = gameId ? retryStateByGame.get(gameId) : undefined;

  const query = useQuery({
    queryKey: installStateQueryKey(client, gameId ?? '', options.project),
    queryFn: () => client.invoke('install-state:get', options.project ?? { gameId: gameId ?? '' }),
    enabled,
    staleTime: 5_000,
    refetchOnMount: true,
    retry: false,
  });

  const refresh = useCallback(async () => {
    if (!gameId) return;
    await queryClient.invalidateQueries({
      queryKey: installStateQueryKey(client, gameId, options.project),
    });
  }, [client, gameId, options.project, queryClient]);

  const retryInstall = useCallback(async (): Promise<InstallRetryResult> => {
    if (!gameId) {
      return { ok: false, reason: 'No game loaded.' };
    }
    if (inFlightGamesRef.current.has(gameId)) {
      return { ok: false, reason: 'Install retry already running.' };
    }
    const queryKey = installStateQueryKey(client, gameId, options.project);
    const existing = queryClient.getQueryData<InstallState>(queryKey);
    if (existing?.status === 'installing') {
      return { ok: false, reason: 'Install retry already running.' };
    }

    inFlightGamesRef.current.add(gameId);
    setRetryStateByGame((current) => {
      const next = new Map(current);
      next.set(gameId, { retrying: true, error: null });
      return next;
    });
    const now = new Date().toISOString();
    queryClient.setQueryData<InstallState>(queryKey, {
      status: 'installing',
      lastUpdate: now,
      startedAt: now,
    });

    try {
      const result = await client.invoke('install-state:retry', {
        gameId,
      });
      if (result.ok) {
        queryClient.setQueryData<InstallState>(queryKey, {
          status: 'ok',
          lastUpdate: new Date().toISOString(),
        });
      } else if ('alreadyInstalling' in result && result.alreadyInstalling) {
        queryClient.setQueryData<InstallState>(queryKey, {
          status: 'installing',
          reason: result.reason,
          lastUpdate: new Date().toISOString(),
          pid: result.pid,
          startedAt: result.startedAt,
        });
        setRetryStateByGame((current) => {
          const next = new Map(current);
          next.set(gameId, { retrying: true, error: result.reason });
          return next;
        });
      } else {
        queryClient.setQueryData<InstallState>(queryKey, {
          status: 'failed',
          reason: result.reason,
          occurredAt: new Date().toISOString(),
          lastUpdate: new Date().toISOString(),
        });
        setRetryStateByGame((current) => {
          const next = new Map(current);
          next.set(gameId, { retrying: true, error: result.reason });
          return next;
        });
      }
      await queryClient.invalidateQueries({ queryKey });
      return result;
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      queryClient.setQueryData<InstallState>(queryKey, {
        status: 'failed',
        reason,
        occurredAt: new Date().toISOString(),
        lastUpdate: new Date().toISOString(),
      });
      setRetryStateByGame((current) => {
        const next = new Map(current);
        next.set(gameId, { retrying: true, error: reason });
        return next;
      });
      await queryClient.invalidateQueries({ queryKey });
      return { ok: false, reason };
    } finally {
      inFlightGamesRef.current.delete(gameId);
      setRetryStateByGame((current) => {
        const next = new Map(current);
        const previous = next.get(gameId);
        next.set(gameId, { retrying: false, error: previous?.error ?? null });
        return next;
      });
    }
  }, [client, gameId, options.project, queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading || query.isFetching,
    isRetrying: currentRetryState?.retrying ?? false,
    retryError: currentRetryState?.error ?? null,
    retryInstall,
    refresh,
  };
}
